import { Injectable } from "@angular/core";
import { Observable, timer, shareReplay, switchMap, of } from "rxjs";

const DEFAULT_CACHE_TIME = 1000;

@Injectable({ providedIn: "root" })
export class CacheService {
  private cache = new Map<string, { data: any; expiration: number }>();
  private cache$ = new Map<
    string,
    { observable: Observable<any>; expiration: number }
  >();

  async getCachedData<T>(
    key: string,
    fetchFn: () => Promise<T>,
    cacheTime = DEFAULT_CACHE_TIME,
  ): Promise<T> {
    const now = Date.now();
    const cachedEntry = this.cache.get(key);

    if (cachedEntry && cachedEntry.expiration > now) {
      return cachedEntry.data;
    }

    const data = await fetchFn();
    this.cache.set(key, { data: data, expiration: now + cacheTime });

    return data;
  }

  getCachedData$<T>(
    key: string,
    fetchFn: () => Observable<T>,
    cacheTime = DEFAULT_CACHE_TIME,
  ): Observable<T> {
    const now = Date.now();
    const cachedEntry = this.cache$.get(key);

    if (cachedEntry && cachedEntry.expiration > now) {
      return cachedEntry.observable;
    }

    const observable = fetchFn().pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      switchMap((data) =>
        timer(cacheTime).pipe(
          switchMap(() => {
            this.cache.delete(key);
            return of(data);
          }),
        ),
      ),
    );

    this.cache$.set(key, {
      observable: observable,
      expiration: now + cacheTime,
    });
    return observable;
  }

  clearCache(key: string): void {
    this.cache.delete(key);
  }

  clearAllCache(): void {
    this.cache.clear();
  }
}
